import { useCallback, useEffect, useRef, useState } from "react";
import { isHistoricalChartRange } from "../constants/chartHistorical";

/** Tick for scrolling x-windows on live charts (1 Hz). */
export function useChartPause(options?: { freezeClock?: boolean }) {
  const freezeClock = options?.freezeClock ?? false;
  const [paused, setPaused] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (freezeClock) setNowMs(Date.now());
  }, [freezeClock]);

  useEffect(() => {
    if (paused || freezeClock) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [paused, freezeClock]);

  const togglePause = useCallback(() => setPaused((p) => !p), []);

  return { paused, togglePause, nowMs, setPaused };
}

/** Snapshot latest value when live; hold while paused. */
export function useFrozenWhilePaused<T>(paused: boolean, value: T): T {
  const ref = useRef(value);
  if (!paused) ref.current = value;
  return paused ? ref.current : value;
}

/** Stop Plotly.react bumps while paused so zoom/pan is preserved. */
export function useLockedRevision(paused: boolean, revision: string): string {
  const ref = useRef(revision);
  if (!paused) ref.current = revision;
  return ref.current;
}

/** Freeze scrolling clock for user pause or x-axis horizons longer than 1h. */
export function useEffectiveChartNow(
  userPaused: boolean,
  nowMs: number,
  rangeMs: number | null | undefined,
) {
  const historicalMode = isHistoricalChartRange(rangeMs);
  const chartFrozen = userPaused || historicalMode;
  const frozenNowRef = useRef(nowMs);

  useEffect(() => {
    if (historicalMode) frozenNowRef.current = nowMs;
  }, [historicalMode, nowMs]);

  if (!chartFrozen) frozenNowRef.current = nowMs;
  const effectiveNowMs = chartFrozen ? frozenNowRef.current : nowMs;

  return { historicalMode, chartFrozen, effectiveNowMs };
}
